import { clsx } from 'clsx';
import { Icon } from './Icon';
import type { ReactNode } from 'react';

/**
 * TopBar — the desk's content header (deskkit.topbar, §13.3).
 *
 * Three rules from §13.3 that are easy to violate by accident:
 *
 *  - Search is a FIELD, not an icon. It opens an overlay search scoped to this
 *    canvas's own entities and this role.
 *  - The bell opens the role's notifications. A permanently-dotted bell that
 *    opens nothing is a dead control and violates §8 — so `onBell` is required
 *    whenever the bell is shown, and the dot is driven by real unread state
 *    rather than being decoration.
 *  - EXACTLY ONE contextual primary action. If a screen seems to want two, one
 *    of them belongs in the pane foot or a row action.
 *
 * Both search and bell open CENTRED MODALS, never side panels — §13.15 allows
 * exactly one side surface, the rail.
 *
 * Controls here are 40px (§13.1), narrower than the kit's default 48px button;
 * kit.css does that contextually via `.topbar .btn`, so nothing is passed in.
 */

interface TopBarProps {
  title: ReactNode;
  sub?: ReactNode;
  /** Placeholder text. Pass `null` to omit search entirely. */
  search?: string | null;
  onSearch?: () => void;
  /**
   * Makes the search field a LIVE INPUT rather than an overlay trigger.
   *
   * §13.3's search is a field that opens an overlay scoped to the canvas — right
   * for a screen whose subject is not a list. A list screen is the other case:
   * the thing being searched is already on screen, and an overlay that reopens
   * it is a detour. Pass a value and a change handler and the same `.srch` box
   * types in place; pass neither and it stays the overlay button.
   */
  searchValue?: string;
  onSearchChange?: (next: string) => void;
  /**
   * The canvas's own search clamp. The kit clamps every bar at 200/170/340;
   * the nurse canvas draws 260/170/260 on every board, and reception's desk
   * bar is narrower still. A page says which shape its canvas uses rather than
   * each lane writing the override into its own CSS.
   */
  searchSize?: 'default' | 'narrow';
  /** Omit `onBell` to hide the bell rather than rendering a dead one. */
  onBell?: () => void;
  /** Drives the bell dot. Decoration is not a valid reason to show it. */
  hasUnread?: boolean;
  /**
   * The single contextual primary.
   *
   * `disabled` is for a verb that EXISTS but cannot run yet — the invoice with
   * an undated batch line. The screen must say why somewhere the reader can
   * see (Receive prints which lines still need a date); a button that looks
   * live and silently does nothing is the dead control §8 forbids, and so is
   * one that greys out with no explanation.
   */
  primary?: { label: ReactNode; onClick: () => void; disabled?: boolean };
  /**
   * The patient file's ANCHORED ACTION PAIR — `Issue ▾` directly beneath the
   * primary, stacked in one 186px column.
   *
   * This does not break "exactly one primary": there is still one, and the
   * second control is a MENU, not a rival verb. The file is the one surface
   * that needs it, because issuing a certificate or a report is something you
   * do to a patient rather than to a visit, so it has nowhere else to live —
   * the pane belongs to the selected row, and a row action would tie it to one
   * visit. It is anchored on all seven file states (§13.2).
   */
  secondary?: { label: ReactNode; onClick: () => void };
  /** Extra controls between the bell and the primary — used sparingly. */
  extra?: ReactNode;
  className?: string;
}

export function TopBar({
  title,
  sub,
  search = 'Search…',
  onSearch,
  searchValue,
  onSearchChange,
  searchSize,
  onBell,
  hasUnread = false,
  primary,
  secondary,
  extra,
  className,
}: TopBarProps) {
  return (
    <div className={clsx('topbar', className)}>
      <div>
        <p className="t-dxl">{title}</p>
        {sub ? <p className="t-sec mut">{sub}</p> : null}
      </div>

      <div className="tb-actions">
        {/* The search field carries NO inline flex. deskkit's generator writes
            `flex:1 1 170px;min-width:150px;max-width:380px` onto every board, and
            the Doctor shell then overrides all three with `!important` — so those
            numbers were never the shipped sizing. The real clamp, and why the row
            has to wrap rather than squeeze, is in kit-extras.css. */}
        {search && onSearchChange ? (
          <div className={clsx('srch', searchSize === 'narrow' && 'srch-narrow')} style={{ margin: 0 }}>
            <Icon name="search" />
            <input
              className="grow"
              value={searchValue ?? ''}
              placeholder={search}
              onChange={(e) => onSearchChange(e.target.value)}
              aria-label={search}
              /* The input keeps its intrinsic width — that is what sizes the
                 field from its own prompt, the way the boards draw it. It may
                 still shrink (`min-width: 0`), and the field around it is
                 capped, so a long prompt cannot push the row. */
              style={{ background: 'transparent', border: 'none', outline: 'none',
                color: 'inherit', font: 'inherit', minWidth: 0 }}
            />
          </div>
        ) : search && onSearch ? (
          <button type="button" className={clsx('srch', searchSize === 'narrow' && 'srch-narrow')} onClick={onSearch} style={{ margin: 0 }}>
            <Icon name="search" />
            <span className="grow" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {search}
            </span>
          </button>
        ) : null}

        {onBell ? (
          <button type="button" className="iconbtn" onClick={onBell} aria-label="Notifications">
            <Icon name="bell" />
            {hasUnread ? <span className="dot" /> : null}
          </button>
        ) : null}

        {extra}

        {/* THE ACTION PAIR SITS ON ONE ROW, secondary first.
            The canvas stacks them in a 186px column under the search field.
            Side by side reads better at the desk: stacked, the outline button
            takes the full width of the primary and competes with it, and the
            two-row block makes the whole header taller than the title needs.
            Left-to-right keeps the primary last, where the eye finishes, and
            the menu-shaped control stays visibly the lesser of the two.
            (Owner-approved deviation — the only one on this screen.) */}
        {secondary ? (
          <button type="button" className="btn btn-outline" onClick={secondary.onClick}>
            {secondary.label}
          </button>
        ) : null}

        {primary ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={primary.onClick}
            disabled={primary.disabled}
          >
            {primary.label}
          </button>
        ) : null}
      </div>
    </div>
  );
}
